import { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import AudioContextManager from '@/utils/audioContextManager';
import { usePlayerCoordination } from '@/contexts/AudioPlayerCoordinator';

interface SeamlessAudioManagerProps {
  mainAudioUrl?: string;
  backgroundAudioUrl?: string;
  backgroundVolume?: number;
  autoPlayBackground?: boolean;
  lazyLoad?: boolean;
}

export const useSeamlessAudioManager = ({
  mainAudioUrl,
  backgroundAudioUrl,
  backgroundVolume = 0.5,
  autoPlayBackground = true,
  lazyLoad = false
}: SeamlessAudioManagerProps) => {
  // Generate unique instance ID
  const instanceIdRef = useRef<string>(`audio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  // HTML5 Audio element for main audio
  const mainAudioRef = useRef<HTMLAudioElement | null>(null);

  // Web Audio API refs for background mixing
  const audioContextRef = useRef<AudioContext | null>(null);
  const backgroundGainNodeRef = useRef<GainNode | null>(null);
  const backgroundSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const backgroundBufferRef = useRef<AudioBuffer | null>(null);
  const hasAcquiredContextRef = useRef<boolean>(false);

  // Track if instance is active (not hidden)
  const isActiveInstanceRef = useRef<boolean>(true);

  // Initialization lock to prevent concurrent initialization
  const isInitializingRef = useRef<boolean>(false);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);

  // Lazy loading state
  const hasRequestedPlaybackRef = useRef<boolean>(false);

  // Debounce timer for URL changes
  const urlDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track last initialized URL
  const lastInitializedUrlRef = useRef<string | null>(null);

  // Track current background volume for initialization
  const backgroundVolumeRef = useRef<number>(backgroundVolume);

  // Function refs to break circular dependencies
  const startVoiceFadeoutRef = useRef<(() => void) | null>(null);
  const startBackgroundFadeoutRef = useRef<(() => void) | null>(null);
  const stopBackgroundAudioRef = useRef<(() => void) | null>(null);

  // State management
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMainAudioReady, setIsMainAudioReady] = useState(false);
  const [isBackgroundAudioReady, setIsBackgroundAudioReady] = useState(false);
  const [isBackgroundAudioLoading, setIsBackgroundAudioLoading] = useState(false);
  const [isBackgroundPlaying, setIsBackgroundPlaying] = useState(false);
  const [isBackgroundMuted, setIsBackgroundMuted] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [savedBackgroundVolume, setSavedBackgroundVolume] = useState(0.25);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fadeoutStartTimeRef = useRef<number | null>(null);
  const [isVoiceFadingOut, setIsVoiceFadingOut] = useState(false);
  const voiceFadeoutIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const voiceFadeoutStartVolumeRef = useRef<number>(1);

  // Extension state for 3-second fadeout after voice ends
  const extensionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const voiceEndTimeRef = useRef<number>(0);

  // Extract stable URL (without token) to prevent unnecessary re-initialization
  const stableAudioUrl = useMemo(() => {
    if (!mainAudioUrl) return null;

    // For streaming URLs with tokens, use base URL without query params
    if (mainAudioUrl.includes('/audio/stream/') && mainAudioUrl.includes('?token=')) {
      const baseUrl = mainAudioUrl.split('?')[0];
      return baseUrl;
    }

    return mainAudioUrl;
  }, [mainAudioUrl]);

  // Initialize Web Audio Context using shared manager
  const initializeAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = AudioContextManager.acquire();
      hasAcquiredContextRef.current = true;
      backgroundGainNodeRef.current = audioContextRef.current.createGain();
      // Initialize with current background volume instead of default 1.0
      backgroundGainNodeRef.current.gain.value = backgroundVolumeRef.current;
      backgroundGainNodeRef.current.connect(audioContextRef.current.destination);
      console.log(`🎵 [${instanceIdRef.current}] Acquired shared AudioContext with volume:`, backgroundVolumeRef.current);
    }
    return audioContextRef.current;
  }, []);

  // Load background audio buffer
  const loadBackgroundAudio = useCallback(async () => {
    if (!backgroundAudioUrl) {
      setIsBackgroundAudioReady(false);
      setIsBackgroundAudioLoading(false);
      return;
    }

    // Set loading state before starting fetch
    setIsBackgroundAudioLoading(true);

    try {
      const audioContext = initializeAudioContext();
      const response = await fetch(backgroundAudioUrl, { mode: 'cors', credentials: 'include' });

      if (!response.ok) {
        console.warn(`⚠️ Background audio not available (${response.status})`);
        setIsBackgroundAudioReady(false);
        setIsBackgroundAudioLoading(false);
        return;
      }

      const arrayBuffer = await response.arrayBuffer();
      backgroundBufferRef.current = await audioContext.decodeAudioData(arrayBuffer);
      setIsBackgroundAudioReady(true);
      setIsBackgroundAudioLoading(false);
      console.log('✅ Background audio loaded successfully');
    } catch (error) {
      console.warn('⚠️ Background audio not available', error);
      setIsBackgroundAudioReady(false);
      setIsBackgroundAudioLoading(false);
    }
  }, [backgroundAudioUrl, initializeAudioContext]);

  // Initialize main audio element
  const initializeMainAudio = useCallback(async (urlToLoad: string): Promise<void> => {
    // Check initialization lock
    if (isInitializingRef.current) {
      if (initializationPromiseRef.current) {
        await initializationPromiseRef.current;
      }
      return;
    }

    // Check if already initialized
    if (lastInitializedUrlRef.current === urlToLoad && mainAudioRef.current) {
      return;
    }

    isInitializingRef.current = true;

    const initPromise = (async () => {
      try {
        // Safe cleanup of previous audio
        if (mainAudioRef.current) {
          const oldAudio = mainAudioRef.current;
          oldAudio.volume = 0;
          oldAudio.pause();
          oldAudio.src = '';
          oldAudio.onloadedmetadata = null;
          oldAudio.oncanplay = null;
          oldAudio.ontimeupdate = null;
          oldAudio.onended = null;
          oldAudio.onplay = null;
          oldAudio.onpause = null;
          oldAudio.onerror = null;
          mainAudioRef.current = null;
        }

        setIsMainAudioReady(false);

        // Create new audio element
        const audio = new Audio();

        // Detect URL type
        const isHttpUrl = urlToLoad.startsWith('http://') || urlToLoad.startsWith('https://');
        const isBlobUrl = urlToLoad.startsWith('blob:');
        const isRelativeUrl = urlToLoad.startsWith('/');

        if (isHttpUrl || isRelativeUrl) {
          audio.crossOrigin = 'use-credentials';
        }

        audio.preload = 'metadata';

        // Event listeners
        audio.addEventListener('loadedmetadata', () => {
          const voiceDuration = audio.duration || 0;

          // If background music is configured, pre-calculate duration with 3-second extension
          if (backgroundAudioUrl) {
            const EXTENSION_DURATION = 3.0; // seconds
            const totalDuration = voiceDuration + EXTENSION_DURATION;
            setDuration(totalDuration);
            console.log(`🎵 [${instanceIdRef.current}] Duration pre-calculated with extension: ${totalDuration.toFixed(1)}s (${voiceDuration.toFixed(1)}s voice + ${EXTENSION_DURATION}s fadeout)`);
          } else {
            setDuration(voiceDuration);
          }

          setIsMainAudioReady(true);
        });

        audio.addEventListener('canplay', () => {
          if (!isMainAudioReady) {
            setIsMainAudioReady(true);
          }
        });

        audio.addEventListener('timeupdate', () => {
          setCurrentTime(audio.currentTime);

          // If no background music and within last 3 seconds, start voice fadeout
          if (!backgroundAudioUrl && audio.duration && !isVoiceFadingOut) {
            const timeRemaining = audio.duration - audio.currentTime;
            const FADEOUT_TRIGGER_TIME = 3.0; // Start fadeout 3 seconds before end

            if (timeRemaining <= FADEOUT_TRIGGER_TIME && timeRemaining > 0) {
              console.log(`🎤 [${instanceIdRef.current}] Triggering voice fadeout (${timeRemaining.toFixed(1)}s remaining)`);
              startVoiceFadeoutRef.current?.();
            }
          }
        });

        audio.addEventListener('ended', () => {
          const voiceDuration = audio.duration || 0;
          voiceEndTimeRef.current = voiceDuration;
          setCurrentTime(voiceDuration);

          // Check if background music was configured
          if (backgroundAudioUrl) {
            // Don't stop playing yet - background will continue for 3 more seconds
            const EXTENSION_DURATION = 3.0; // seconds
            console.log(`🎵 [${instanceIdRef.current}] Voice ended at ${voiceDuration.toFixed(1)}s - background will continue for ${EXTENSION_DURATION}s`);

            // Duration already includes extension (pre-calculated on load)
            // No need to update duration here

            // Start the 3-second fadeout
            startBackgroundFadeoutRef.current?.();

            // Simulate extended playback with progress updates
            const extensionStartTime = Date.now();

            extensionIntervalRef.current = setInterval(() => {
              const elapsed = (Date.now() - extensionStartTime) / 1000;

              if (elapsed >= EXTENSION_DURATION) {
                // Extension complete - stop everything
                if (extensionIntervalRef.current) {
                  clearInterval(extensionIntervalRef.current);
                  extensionIntervalRef.current = null;
                }
                setIsPlaying(false);
                setCurrentTime(voiceDuration + EXTENSION_DURATION);
                console.log(`🎵 [${instanceIdRef.current}] Session complete at ${(voiceDuration + EXTENSION_DURATION).toFixed(1)}s (voice + ${EXTENSION_DURATION}s fadeout)`);
              } else {
                // Update current time to show extension progress
                setCurrentTime(voiceDuration + elapsed);
              }
            }, 100); // Update every 100ms for smooth progress bar

          } else {
            // No background music configured - stop immediately
            setIsPlaying(false);
            console.log(`🎤 [${instanceIdRef.current}] Voice ended - no background music configured`);
            stopBackgroundAudioRef.current?.();
          }
        });

        audio.addEventListener('play', () => {
          setIsPlaying(true);
        });

        audio.addEventListener('pause', () => {
          setIsPlaying(false);
        });

        audio.addEventListener('error', (e) => {
          console.error(`❌ [${instanceIdRef.current}] Audio error:`, e);
          setIsMainAudioReady(false);
          setIsPlaying(false);
        });

        // Set source - use original mainAudioUrl with token
        audio.src = mainAudioUrl || urlToLoad;
        audio.load();

        mainAudioRef.current = audio;
        lastInitializedUrlRef.current = urlToLoad;
      } catch (error) {
        console.error(`❌ [${instanceIdRef.current}] Initialization error:`, error);
        setIsMainAudioReady(false);
      } finally {
        isInitializingRef.current = false;
        initializationPromiseRef.current = null;
      }
    })();

    initializationPromiseRef.current = initPromise;
    await initPromise;
  }, [mainAudioUrl, backgroundAudioUrl, isVoiceFadingOut]);

  // Background audio functions
  const playBackgroundAudio = useCallback(() => {
    const audioContext = audioContextRef.current;
    const backgroundBuffer = backgroundBufferRef.current;
    const backgroundGainNode = backgroundGainNodeRef.current;

    if (!audioContext || !backgroundBuffer || !backgroundGainNode) {
      return null;
    }

    // Stop existing source
    if (backgroundSourceRef.current) {
      try {
        backgroundSourceRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }

    // Create new source
    const source = audioContext.createBufferSource();
    source.buffer = backgroundBuffer;
    source.loop = true;
    source.connect(backgroundGainNode);

    backgroundSourceRef.current = source;

    try {
      source.start();
      setIsBackgroundPlaying(true);
      return source;
    } catch (error) {
      setIsBackgroundPlaying(false);
      return null;
    }
  }, []);

  const stopBackgroundAudio = useCallback(() => {
    // Clear fadeout timer if active
    if (fadeoutTimerRef.current) {
      clearTimeout(fadeoutTimerRef.current);
      fadeoutTimerRef.current = null;
    }

    setIsFadingOut(false);
    fadeoutStartTimeRef.current = null;

    if (backgroundSourceRef.current) {
      try {
        backgroundSourceRef.current.stop();
        backgroundSourceRef.current = null;
        setIsBackgroundPlaying(false);
      } catch (e) {
        // Already stopped
      }
    }

    // Reset gain to original value for next playback
    if (backgroundGainNodeRef.current && audioContextRef.current) {
      backgroundGainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
      backgroundGainNodeRef.current.gain.setValueAtTime(
        backgroundVolume,
        audioContextRef.current.currentTime
      );
    }
  }, [backgroundVolume]);

  // Assign to ref for use in circular dependency scenarios
  stopBackgroundAudioRef.current = stopBackgroundAudio;

  const pauseBackgroundAudio = useCallback(() => {
    if (backgroundGainNodeRef.current && !isBackgroundMuted) {
      setSavedBackgroundVolume(backgroundGainNodeRef.current.gain.value);
      backgroundGainNodeRef.current.gain.value = 0;
      setIsBackgroundMuted(true);
    }
  }, [isBackgroundMuted]);

  const resumeBackgroundAudio = useCallback(() => {
    if (backgroundGainNodeRef.current && isBackgroundMuted) {
      backgroundGainNodeRef.current.gain.value = savedBackgroundVolume;
      setIsBackgroundMuted(false);
    }
  }, [isBackgroundMuted, savedBackgroundVolume]);

  const startBackgroundFadeout = useCallback(() => {
    const audioContext = audioContextRef.current;
    const backgroundGainNode = backgroundGainNodeRef.current;
    const backgroundSource = backgroundSourceRef.current;

    // Comprehensive logging to identify which ref is null
    console.log(`🎵 [${instanceIdRef.current}] startBackgroundFadeout called`, {
      hasAudioContext: !!audioContext,
      hasBackgroundGainNode: !!backgroundGainNode,
      hasBackgroundSource: !!backgroundSource,
      isFadingOut: isFadingOut,
      backgroundAudioUrl: backgroundAudioUrl,
      isBackgroundPlaying: isBackgroundPlaying
    });

    // Check each condition separately with specific error messages
    if (!audioContext) {
      console.error(`❌ [${instanceIdRef.current}] Cannot start fadeout: audioContext is null`);
      return;
    }

    if (!backgroundGainNode) {
      console.error(`❌ [${instanceIdRef.current}] Cannot start fadeout: backgroundGainNode is null`);
      return;
    }

    if (!backgroundSource) {
      console.error(`❌ [${instanceIdRef.current}] Cannot start fadeout: backgroundSource is null`);
      return;
    }

    if (isFadingOut) {
      console.warn(`⚠️ [${instanceIdRef.current}] Cannot start fadeout: already fading out`);
      return;
    }

    setIsFadingOut(true);

    const FADEOUT_DURATION = 3.0; // 3 seconds
    const currentTime = audioContext.currentTime;
    const currentGain = backgroundGainNode.gain.value;

    fadeoutStartTimeRef.current = currentTime;

    // Cancel any scheduled gain changes
    backgroundGainNode.gain.cancelScheduledValues(currentTime);
    backgroundGainNode.gain.setValueAtTime(currentGain, currentTime);

    // Use exponentialRampToValueAtTime for natural fadeout
    // Can't target exactly 0, use 0.0001 (essentially silent)
    backgroundGainNode.gain.exponentialRampToValueAtTime(
      0.0001,
      currentTime + FADEOUT_DURATION
    );

    console.log(`🎵 [${instanceIdRef.current}] Starting 3-second background fadeout`, {
      currentGain,
      startTime: currentTime,
      endTime: currentTime + FADEOUT_DURATION
    });

    // Stop background after fadeout completes
    fadeoutTimerRef.current = setTimeout(() => {
      stopBackgroundAudio();
      setIsFadingOut(false);
      fadeoutStartTimeRef.current = null;
      console.log(`🎵 [${instanceIdRef.current}] Background fadeout complete`);
    }, FADEOUT_DURATION * 1000);

  }, [isFadingOut, stopBackgroundAudio, backgroundAudioUrl, isBackgroundPlaying]);

  // Assign to ref for use in circular dependency scenarios
  startBackgroundFadeoutRef.current = startBackgroundFadeout;

  const startVoiceFadeout = useCallback(() => {
    const audio = mainAudioRef.current;

    if (!audio || isVoiceFadingOut) {
      console.warn(`⚠️ [${instanceIdRef.current}] Cannot start voice fadeout: ${!audio ? 'audio is null' : 'already fading out'}`);
      return;
    }

    console.log(`🎤 [${instanceIdRef.current}] Starting 3-second voice fadeout`, {
      currentTime: audio.currentTime,
      duration: audio.duration,
      currentVolume: audio.volume
    });

    setIsVoiceFadingOut(true);
    voiceFadeoutStartVolumeRef.current = audio.volume;

    const FADEOUT_DURATION_MS = 3000; // 3 seconds
    const INTERVAL_MS = 50; // Update every 50ms for smooth fadeout
    const steps = FADEOUT_DURATION_MS / INTERVAL_MS;
    const volumeDecrement = voiceFadeoutStartVolumeRef.current / steps;

    let currentStep = 0;

    voiceFadeoutIntervalRef.current = setInterval(() => {
      const audio = mainAudioRef.current;
      if (!audio) {
        // Audio element was removed, cleanup
        if (voiceFadeoutIntervalRef.current) {
          clearInterval(voiceFadeoutIntervalRef.current);
          voiceFadeoutIntervalRef.current = null;
        }
        setIsVoiceFadingOut(false);
        return;
      }

      currentStep++;
      const newVolume = Math.max(0, voiceFadeoutStartVolumeRef.current - (volumeDecrement * currentStep));
      audio.volume = newVolume;

      // Check if fadeout is complete
      if (currentStep >= steps || newVolume <= 0) {
        if (voiceFadeoutIntervalRef.current) {
          clearInterval(voiceFadeoutIntervalRef.current);
          voiceFadeoutIntervalRef.current = null;
        }
        audio.volume = 0;
        setIsVoiceFadingOut(false);
        console.log(`🎤 [${instanceIdRef.current}] Voice fadeout complete`);
      }
    }, INTERVAL_MS);

  }, [isVoiceFadingOut]);

  // Assign to ref for use in circular dependency scenarios
  startVoiceFadeoutRef.current = startVoiceFadeout;

  // Multi-instance coordinator integration
  const pause = useCallback(() => {
    if (mainAudioRef.current) {
      mainAudioRef.current.pause();
    }

    // Cancel background fadeout if active
    if (isFadingOut && fadeoutTimerRef.current) {
      clearTimeout(fadeoutTimerRef.current);
      fadeoutTimerRef.current = null;
      setIsFadingOut(false);
    }

    // Cancel voice fadeout if active and reset volume
    if (isVoiceFadingOut && voiceFadeoutIntervalRef.current) {
      clearInterval(voiceFadeoutIntervalRef.current);
      voiceFadeoutIntervalRef.current = null;
      setIsVoiceFadingOut(false);

      // Reset volume to original
      if (mainAudioRef.current) {
        mainAudioRef.current.volume = voiceFadeoutStartVolumeRef.current;
      }
    }
  }, [isFadingOut, isVoiceFadingOut]);

  const stop = useCallback(() => {
    if (mainAudioRef.current) {
      mainAudioRef.current.pause();
      mainAudioRef.current.currentTime = 0;
      setCurrentTime(0);
    }

    stopBackgroundAudio();
    setIsPlaying(false);
  }, [stopBackgroundAudio]);

  // Stabilize controls object to prevent unnecessary re-registrations
  const stableControls = useMemo(() => ({ pause, stop }), [pause, stop]);

  // Register with coordinator
  const coordination = usePlayerCoordination(instanceIdRef.current, stableControls);

  // Seamless play function
  const play = useCallback(async () => {
    console.log('[useSeamlessAudioManager] Play called:', {
      lazyLoad,
      hasRequestedPlayback: hasRequestedPlaybackRef.current,
      stableAudioUrl: stableAudioUrl ? stableAudioUrl.substring(0, 50) + '...' : null,
      isMainAudioReady,
      mainAudioExists: !!mainAudioRef.current
    });

    // Lazy loading - initialize on first play
    if (lazyLoad && !hasRequestedPlaybackRef.current) {
      hasRequestedPlaybackRef.current = true;

      if (stableAudioUrl) {
        console.log('[useSeamlessAudioManager] Lazy load: initializing audio');
        await initializeMainAudio(stableAudioUrl);
      } else {
        console.warn('[useSeamlessAudioManager] Lazy load: no URL available');
        return;
      }
    }

    // Fallback: If audio should be ready but isn't, try initializing
    if (!mainAudioRef.current && stableAudioUrl && !isInitializingRef.current) {
      console.log('[useSeamlessAudioManager] Fallback: audio not initialized, attempting initialization');
      await initializeMainAudio(stableAudioUrl);
    }

    const mainAudio = mainAudioRef.current;

    if (!mainAudio) {
      console.warn('[useSeamlessAudioManager] Cannot play: audio not initialized');
      return;
    }

    // For non-lazy load, check if audio is ready
    if (!lazyLoad && !isMainAudioReady) {
      console.warn('[useSeamlessAudioManager] Audio not ready yet');
      return;
    }

    try {
      // Notify coordinator that this player is starting
      coordination?.notifyPlayStarted();

      // Resume AudioContext if suspended
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Start background audio if enabled
      if (autoPlayBackground && backgroundAudioUrl && isBackgroundAudioReady && !backgroundSourceRef.current) {
        playBackgroundAudio();
      }

      // Play main audio
      if (mainAudio === mainAudioRef.current) {
        await mainAudio.play();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        setIsPlaying(false);
        return;
      }

      console.error(`❌ [${instanceIdRef.current}] Play failed:`, error);
      setIsPlaying(false);
    }
  }, [isMainAudioReady, autoPlayBackground, backgroundAudioUrl, isBackgroundAudioReady, playBackgroundAudio, lazyLoad, stableAudioUrl, initializeMainAudio, coordination]);

  // Notify coordinator when playback ends
  useEffect(() => {
    if (!isPlaying) {
      coordination?.notifyPlayEnded();
    }
  }, [isPlaying, coordination]);

  // Monitor AudioContext state during playback and resume if suspended
  // Browsers can suspend AudioContext when tab becomes inactive or for power saving
  useEffect(() => {
    if (!isPlaying || !audioContextRef.current) {
      return;
    }

    const checkAndResumeContext = async () => {
      const ctx = audioContextRef.current;
      if (ctx && ctx.state === 'suspended') {
        console.log(`🔊 [${instanceIdRef.current}] AudioContext suspended during playback - resuming...`);
        try {
          await ctx.resume();
          console.log(`🔊 [${instanceIdRef.current}] AudioContext resumed successfully`);
        } catch (error) {
          console.error(`❌ [${instanceIdRef.current}] Failed to resume AudioContext:`, error);
        }
      }
    };

    // Check immediately
    checkAndResumeContext();

    // Check periodically while playing (every 2 seconds)
    const intervalId = setInterval(checkAndResumeContext, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isPlaying]);

  const seekTo = useCallback((time: number) => {
    if (mainAudioRef.current && isMainAudioReady) {
      // Cancel background fadeout if seeking during fadeout
      if (isFadingOut && fadeoutTimerRef.current) {
        clearTimeout(fadeoutTimerRef.current);
        fadeoutTimerRef.current = null;
        setIsFadingOut(false);

        // Reset background gain to normal level
        if (backgroundGainNodeRef.current && audioContextRef.current) {
          const currentGain = backgroundVolume;
          backgroundGainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
          backgroundGainNodeRef.current.gain.setValueAtTime(currentGain, audioContextRef.current.currentTime);
        }
      }

      // Cancel voice fadeout if seeking during fadeout
      if (isVoiceFadingOut && voiceFadeoutIntervalRef.current) {
        clearInterval(voiceFadeoutIntervalRef.current);
        voiceFadeoutIntervalRef.current = null;
        setIsVoiceFadingOut(false);

        // Reset volume to original
        mainAudioRef.current.volume = voiceFadeoutStartVolumeRef.current;
      }

      const clampedTime = Math.max(0, Math.min(time, duration));
      mainAudioRef.current.currentTime = clampedTime;
      setCurrentTime(clampedTime);
    }
  }, [isMainAudioReady, duration, isFadingOut, isVoiceFadingOut, backgroundVolume]);

  const updateMainVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);

    if (mainAudioRef.current) {
      mainAudioRef.current.volume = clampedVolume;
    }
  }, []);

  const updatePlaybackRate = useCallback((rate: number) => {
    const clampedRate = Math.max(0.25, Math.min(4, rate));
    setPlaybackRateState(clampedRate);

    if (mainAudioRef.current) {
      mainAudioRef.current.playbackRate = clampedRate;
    }
  }, []);

  const updateBackgroundVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));

    console.log(`🎚️ updateBackgroundVolume called:`, {
      requestedVolume: volume,
      clampedVolume,
      hasGainNode: !!backgroundGainNodeRef.current,
      isBackgroundMuted,
      willApply: !!backgroundGainNodeRef.current && !isBackgroundMuted
    });

    // Always save the volume preference
    setSavedBackgroundVolume(clampedVolume);

    // Apply to gain node if it exists and not muted
    if (backgroundGainNodeRef.current && !isBackgroundMuted) {
      backgroundGainNodeRef.current.gain.value = clampedVolume;
      console.log(`✅ Background volume applied: ${clampedVolume} (gain.value now = ${backgroundGainNodeRef.current.gain.value})`);
    } else {
      console.warn(`⚠️ Cannot apply volume - gainNode: ${!!backgroundGainNodeRef.current}, muted: ${isBackgroundMuted}`);
    }
  }, [isBackgroundMuted]);

  // Load audio when URLs change (with lazy loading and debouncing)
  useEffect(() => {
    console.log('[useSeamlessAudioManager] URL effect triggered:', {
      lazyLoad,
      stableAudioUrl: stableAudioUrl ? stableAudioUrl.substring(0, 50) + '...' : null,
      lastInitializedUrl: lastInitializedUrlRef.current ? lastInitializedUrlRef.current.substring(0, 50) + '...' : null
    });

    if (lazyLoad) {
      console.log('[useSeamlessAudioManager] Lazy load enabled, skipping initialization');
      return;
    }

    if (!stableAudioUrl) {
      console.log('[useSeamlessAudioManager] No URL available, setting ready to false');
      setIsMainAudioReady(false);
      return;
    }

    // Clear existing debounce timer
    if (urlDebounceTimerRef.current) {
      clearTimeout(urlDebounceTimerRef.current);
    }

    // Immediate initialization for first load, debounce for subsequent changes
    if (!lastInitializedUrlRef.current) {
      console.log('[useSeamlessAudioManager] First load - initializing immediately');
      initializeMainAudio(stableAudioUrl);
    } else {
      console.log('[useSeamlessAudioManager] URL changed - debouncing for 300ms');
      urlDebounceTimerRef.current = setTimeout(() => {
        console.log('[useSeamlessAudioManager] Debounce complete - initializing');
        initializeMainAudio(stableAudioUrl);
      }, 300);
    }

    return () => {
      if (urlDebounceTimerRef.current) {
        clearTimeout(urlDebounceTimerRef.current);
      }
    };
  }, [stableAudioUrl, lazyLoad, initializeMainAudio]);

  useEffect(() => {
    loadBackgroundAudio();
  }, [loadBackgroundAudio]);

  // Update background volume ref and apply volume changes
  useEffect(() => {
    backgroundVolumeRef.current = backgroundVolume;
    updateBackgroundVolume(backgroundVolume);
  }, [backgroundVolume, updateBackgroundVolume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isActiveInstanceRef.current = false;

      // Clear fadeout timer
      if (fadeoutTimerRef.current) {
        clearTimeout(fadeoutTimerRef.current);
        fadeoutTimerRef.current = null;
      }

      // Clear voice fadeout interval
      if (voiceFadeoutIntervalRef.current) {
        clearInterval(voiceFadeoutIntervalRef.current);
        voiceFadeoutIntervalRef.current = null;
      }

      // Clear extension timer
      if (extensionIntervalRef.current) {
        clearInterval(extensionIntervalRef.current);
        extensionIntervalRef.current = null;
      }

      // Clear debounce timer
      if (urlDebounceTimerRef.current) {
        clearTimeout(urlDebounceTimerRef.current);
      }

      // Cancel pending initialization
      isInitializingRef.current = false;
      initializationPromiseRef.current = null;

      stop();

      // Release shared AudioContext
      if (hasAcquiredContextRef.current) {
        AudioContextManager.release();
        hasAcquiredContextRef.current = false;
        audioContextRef.current = null;
      }

      if (mainAudioRef.current) {
        mainAudioRef.current.src = '';
        mainAudioRef.current = null;
      }
    };
  }, [stop]);

  return {
    // Core playback controls
    play,
    pause,
    stop,
    seekTo,

    // Property updates
    updateMainVolume,
    updateBackgroundVolume,
    updatePlaybackRate,

    // Background audio controls
    playBackgroundAudio,
    stopBackgroundAudio,
    pauseBackgroundAudio,
    resumeBackgroundAudio,

    // State
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    isMainAudioReady,
    isBackgroundAudioReady,
    isBackgroundAudioLoading,
    isBackgroundPlaying,
    isBackgroundMuted,
    isFadingOut,
    isVoiceFadingOut,

    // Audio context
    initializeAudioContext
  };
};
